
import SyncPdvDesktopService from "../services/SyncPdvDesktopService";
import CaixaSyncService from "../services/CaixaSyncService";
import PdvDesktopAuthService from "../services/PdvDesktopAuthService";
import TerminalConfigService from "../services/TerminalConfigService";

export default {
  name: "PdvDesktopLayout",

  data() {
    return {
      online: typeof navigator !== "undefined" ? navigator.onLine : true,
      terminalNome: "",
      serie: "",
      nomeUsuario: "",
    };
  },

  computed: {
    isDashboard() {
      return (
        this.$route.name === "pdvDashboard" ||
        this.$route.name === "pdvDesktopDashboard"
      );
    },
    mostrarTopbar() {
      // A tela de Nova Venda possui header operacional próprio completo.
      return this.$route.name !== "pdvDesktopNovaVenda";
    },
  },

  async mounted() {
    if (!this.$isDesktop) {
      this.$router.push({ name: "pdvDesktopLogin" });
      return;
    }

    window.addEventListener("online", this._updateStatus);
    window.addEventListener("offline", this._updateStatus);

    await this._loadTerminalInfo();

    if (navigator.onLine) {
      this._autoSyncCaixa();
    }
  },

  beforeDestroy() {
    window.removeEventListener("online", this._updateStatus);
    window.removeEventListener("offline", this._updateStatus);
  },

  methods: {
    _updateStatus() {
      const voltouOnline = !this.online && navigator.onLine;
      this.online = navigator.onLine;
      if (voltouOnline) {
        // Primeiro verifica se o terminal ainda está autorizado, depois sincroniza
        this._verificarAutorizacaoTerminal().then((autorizado) => {
          if (autorizado) {
            this._autoSyncCaixa();
            this._autoSyncNfce();
          }
        });
      }
    },

    async _autoSyncCaixa() {
      try {
        await CaixaSyncService.sincronizarCaixasPendentes();
      } catch (e) {
        console.warn("[PdvDesktopLayout] Auto-sync caixa falhou:", e.message);
      }
    },

    /**
     * Consulta o backend para verificar se este dispositivo ainda está
     * vinculado ao terminal. Se não estiver, exibe aviso e redireciona
     * para a tela de configuração do terminal.
     *
     * Retorna true se autorizado (ou em caso de falha de rede), false se
     * o acesso foi bloqueado e o redirecionamento já foi acionado.
     */
    async _verificarAutorizacaoTerminal() {
      try {
        const { autorizado, motivo, code } = await TerminalConfigService.verificarVinculoOnline();
        if (autorizado) return true;

        if (TerminalConfigService.isCodigoSessaoEmpresaDivergente(code)) {
          await this.$swal.fire({
            icon: "warning",
            title: "Sessão de outra empresa",
            text: motivo,
            confirmButtonText: "Entendi",
          });
          return false;
        }

        // Bloqueia operações online imediatamente, antes de mostrar dialog
        TerminalConfigService.marcarDesvinculado();

        // Impede que a mensagem apareça múltiplas vezes
        if (this._bloqueandoTerminal) return false;
        this._bloqueandoTerminal = true;

        await this.$swal.fire({
          icon: "warning",
          title: TerminalConfigService.isCodigoBloqueioEmpresa(code)
            ? "Operacao bloqueada"
            : "Terminal desvinculado",
          text: motivo || (
            TerminalConfigService.isCodigoBloqueioEmpresa(code)
              ? "A empresa esta bloqueada para operacoes no PDV. Entre em contato com o suporte."
              : "Este terminal foi desvinculado no painel web. Configure novamente o terminal para continuar."
          ),
          confirmButtonText: TerminalConfigService.isCodigoBloqueioEmpresa(code)
            ? "Ver bloqueio"
            : "Configurar terminal",
          allowOutsideClick: false,
          allowEscapeKey: false,
        });

        this._bloqueandoTerminal = false;
        this.$router.push({
          name: TerminalConfigService.isCodigoBloqueioEmpresa(code)
            ? "pdvDesktopBloqueio"
            : "pdvDesktopConfiguracaoTerminal",
        });
        return false;

      } catch (e) {
        console.warn("[PdvDesktopLayout] Erro ao verificar autorização:", e.message);
        return true; // fail-open
      }
    },

    async _autoSyncNfce() {
      try {
        const resultado = await SyncPdvDesktopService.sincronizarNfcesAssinadas();
        if (resultado && resultado.transmitidas > 0) {
          console.info(
            `[PdvDesktopLayout] Auto-sync NFC-e: ${resultado.transmitidas} autorizada(s), ` +
            `${resultado.erros} erro(s).`
          );
        }
      } catch (e) {
        console.warn("[PdvDesktopLayout] Auto-sync NFC-e falhou:", e.message);
      }
    },

    async _loadTerminalInfo() {
      try {
        const api = this.$electron;
        if (!api) return;

        const [info, serie, nomeUsuario] = await Promise.all([
          api.terminal.getInfo(),
          api.config.get("serie_nfe"),
          api.config.get("usuario_nome"),
        ]);

        this.terminalNome = info?.nome || info?.uuid_terminal || "PDV";
        this.serie = serie || "";
        this.nomeUsuario = nomeUsuario || "";
      } catch (e) {
        console.error("[PdvDesktopLayout] Erro ao carregar info do terminal:", e);
      }
    },

    voltarDashboard() {
      this.$router.push({ name: "pdvDesktopDashboard" });
    },

    async sair() {
      const { isConfirmed } = await this.$swal.fire({
        icon: "question",
        title: "Deseja realmente sair do PDV?",
        showCancelButton: true,
        confirmButtonText: "Sim, sair",
        cancelButtonText: "Cancelar",
        reverseButtons: true,
      });

      if (!isConfirmed) return;

      try {
        await PdvDesktopAuthService.logout();
      } catch (_) { /* ignore */ }
      this.$router.push({ name: "pdvDesktopLogin" });
    },

    abrirConfiguracoesDashboard() {
      window.dispatchEvent(new CustomEvent("pdv-dashboard-open-settings"));
    },
  },
};
